/* eslint-disable no-param-reassign */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button',
  'textarea',
  'input',
  'select',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusableElements(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];
  const all = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  return all.filter(
    (el) =>
      !el.hasAttribute('disabled') &&
      el.getAttribute('aria-hidden') !== 'true' &&
      el.tabIndex !== -1,
  );
}

type FocusTrapOptions = {
  initialFocusElement?: HTMLElement | null;
  onEscape?: () => void;
};

type DialogWithTrap = HTMLDialogElement & {
  __focusTrapCleanup__?: () => void;
};

export function attachFocusTrap(
  dialog: DialogWithTrap | HTMLElement,
  { initialFocusElement, onEscape }: FocusTrapOptions = {},
): () => void {
  const previouslyFocused = document.activeElement as HTMLElement | null;

  let focusable = getFocusableElements(dialog);
  const [first] = focusable;

  const focusTarget = initialFocusElement || first || dialog;
  focusTarget?.focus?.();

  const handleKeyDown = (event: KeyboardEvent | Event): void => {
    if (!('key' in event)) return;
    const keyEvent = event as KeyboardEvent;

    if (keyEvent.key === 'Tab') {
      focusable = getFocusableElements(dialog);
      const [firstEl] = focusable;
      const lastEl = focusable.length > 1 ? focusable[focusable.length - 1] : firstEl;

      if (focusable.length === 0) {
        keyEvent.preventDefault();
        (dialog as HTMLElement).focus();
        return;
      }

      const current = document.activeElement as HTMLElement | null;

      if (keyEvent.shiftKey) {
        if (!current || current === firstEl) {
          keyEvent.preventDefault();
          lastEl?.focus?.();
        }
      } else if (!current || current === lastEl) {
        keyEvent.preventDefault();
        firstEl?.focus?.();
      }
    }

    if (keyEvent.key === 'Escape' && onEscape) {
      onEscape();
      previouslyFocused?.focus?.();
    }
  };

  dialog.addEventListener('keydown', handleKeyDown);

  const cleanup = () => {
    dialog.removeEventListener('keydown', handleKeyDown);
    previouslyFocused?.focus?.();
  };

  const dialogWithTrap = dialog as DialogWithTrap;
  if (dialogWithTrap.__focusTrapCleanup__) {
    dialogWithTrap.__focusTrapCleanup__();
  }
  dialogWithTrap.__focusTrapCleanup__ = cleanup;

  return cleanup;
}
